import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Menu, MenuItem, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import StoreIcon from '@mui/icons-material/Store';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DeleteIcon from '@mui/icons-material/Delete';
import { useLocation, useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { SECTIONS, UNITS, amount, needSourceChip, parseNumber, sectionText, unitShort } from '../labels';
import { Columns, EmptyState, Loader, PageHeader, Panel, useLoad, useNotify, useRun, useUndoable } from '../common';
import { useTripPlanning } from '../trips/tripTools';
import { PredictionDialog } from './PredictionDialog';

const GROUP_KEY = 'planner.shoppingGroupBy';
type GroupBy = 'section' | 'store';
const readGroupBy = (): GroupBy => { try { return window.localStorage.getItem(GROUP_KEY) === 'store' ? 'store' : 'section'; } catch { return 'section'; } };

interface ListData { needs: T.ShoppingNeed[]; dueRecurring: number; }

async function loadList(): Promise<ListData> {
  // The count only, not every regular purchase: the server is small
  const [needs, status] = await Promise.all([api.getShoppingNeeds(), api.getShoppingStatus(false)]);
  return { needs, dueRecurring: status.dueRecurringCount };
}

const needAmount = (n: T.ShoppingNeed) => amount(n.quantity, n.unit, n.hasUnquantified);

/** The meals that need it and the note, under the chips. */
function needDetail(n: T.ShoppingNeed): string {
  const parts: string[] = [];
  if (n.source === 'MealPlan' && n.meals.length > 0) parts.push(n.meals.join(', '));
  if (n.note && n.note.trim()) parts.push(n.note);
  return parts.join(' · ');
}

function groupNeeds(needs: T.ShoppingNeed[], by: GroupBy): { key: string; title: string; items: T.ShoppingNeed[] }[] {
  if (by === 'section') {
    return SECTIONS
      .map(s => ({ key: s.value, title: s.text, items: needs.filter(n => n.storeSection === s.value) }))
      .filter(g => g.items.length > 0);
  }
  const stores = Array.from(new Set(needs.filter(n => n.storeName).map(n => n.storeName as string)))
    .sort((a, b) => a.localeCompare(b, 'pt-PT', { sensitivity: 'base' }));
  const bySection = (a: T.ShoppingNeed, b: T.ShoppingNeed) =>
    SECTIONS.findIndex(s => s.value === a.storeSection) - SECTIONS.findIndex(s => s.value === b.storeSection);
  return stores
    .map(store => ({ key: store, title: store, items: needs.filter(n => n.storeName === store).sort(bySection) }))
    .concat([{ key: '', title: 'Qualquer loja', items: needs.filter(n => !n.storeName).sort(bySection) }])
    .filter(g => g.items.length > 0);
}

/** The "to buy" list, grouped by supermarket section. Circle = bought, the rest opens what can be done with it. */
const ShoppingPage: React.FC = () => {
  const navigate = useNavigate();
  const run = useRun();
  const notify = useNotify();
  const offerUndo = useUndoable();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data, setData, error, loading, reload } = useLoad(loadList);
  const planning = useTripPlanning(reload);
  const [groupBy, setGroupBy] = useState<GroupBy>(readGroupBy);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [actionsFor, setActionsFor] = useState<T.ShoppingNeed | null>(null);
  const [editing, setEditing] = useState<T.ShoppingNeed | null>(null);
  const [storeFor, setStoreFor] = useState<{ need: T.ShoppingNeed; stores: T.Store[] } | null>(null);
  const [predicting, setPredicting] = useState<T.ShoppingPrediction | null>(null);
  const location = useLocation();

  // "Leite na lista. Passo a prever?": left by the add page when the app did not see it coming
  const added = (location.state as { added?: T.ShoppingNeed } | null)?.added;
  useEffect(() => {
    if (!added) return;
    navigate(location.pathname, { replace: true, state: null });
    const prediction = added.prediction;
    if (prediction) notify(`${added.name} na lista. Passo a prever?`, { actionText: 'Prever', action: () => setPredicting(prediction) });
    else notify(`${added.name} na lista.`, { severity: 'success' });
  }, [added, location.pathname, navigate, notify]);

  const changeGroupBy = (value: GroupBy | null) => {
    if (!value) return;
    setGroupBy(value);
    try { window.localStorage.setItem(GROUP_KEY, value); } catch { /* not available */ }
  };

  /** "Leite: comprado · Desfazer": no confirmation before, a way back after. */
  const offerReopen = (need: T.ShoppingNeed, what: string) =>
    offerUndo(`${need.name}: ${what}`, () => api.reopenShoppingNeed(need.publicId), reload);

  /** Bought or taken off: the line leaves the list at once, without asking for the list again. */
  const drop = (need: T.ShoppingNeed) =>
    setData(current => current && { ...current, needs: current.needs.filter(n => n.publicId !== need.publicId) });

  const bought = async (need: T.ShoppingNeed) => {
    if (await run(() => api.markShoppingNeedBought(need.publicId))) {
      drop(need);
      offerReopen(need, 'comprado');
    }
  };

  const remove = async (need: T.ShoppingNeed) => {
    if (await run(() => api.cancelShoppingNeed(need.publicId))) {
      drop(need);
      offerReopen(need, 'retirado');
    }
  };

  const choose = async (choice: 'bought' | 'edit' | 'store' | 'item' | 'remove') => {
    const need = actionsFor;
    setActionsFor(null);
    if (!need) return;
    switch (choice) {
      case 'bought': await bought(need); break;
      case 'edit': setEditing(need); break;
      case 'store': {
        const stores = await run(() => api.getStores());
        if (stores) setStoreFor({ need, stores });
        break;
      }
      case 'item': navigate(`/casa/catalogo/${need.catalogItemPublicId}`); break;
      case 'remove': await remove(need); break;
    }
  };

  const assignStore = async (storeId: T.Guid | null | undefined) => {
    const need = storeFor?.need;
    setStoreFor(null);
    if (!need || storeId === undefined) return;
    if (await run(() => api.assignShoppingNeedStore(need.publicId, storeId))) reload();
  };

  const needs = data?.needs ?? [];
  const more = (to: () => void) => () => { setMoreAnchor(null); to(); };

  return (
    <>
      <PageHeader
        eyebrow="Compras"
        title="Por comprar"
        subtitle={needs.length === 0 ? undefined : needs.length === 1 ? '1 artigo' : `${needs.length} artigos`}
        actions={<>
          <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={() => navigate('/casa/compras/da-ementa')} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>Da ementa</Button>
          <Button variant="outlined" startIcon={<LocalShippingOutlinedIcon />} onClick={planning.startNewTrip} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>Nova ida</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/casa/compras/juntar')}>Juntar</Button>
          <IconButton aria-label="Mais opções" onClick={e => setMoreAnchor(e.currentTarget)}><MoreVertIcon /></IconButton>
        </>}
      />
      <Menu anchorEl={moreAnchor} open={moreAnchor !== null} onClose={() => setMoreAnchor(null)}>
        <MenuItem onClick={more(() => navigate('/casa/compras/da-ementa'))}>
          <ListItemText primary="Gerar da ementa" secondary="Junta os ingredientes da semana que faltam" />
        </MenuItem>
        <MenuItem onClick={more(() => navigate('/casa/compras/regulares'))}>
          <ListItemText primary="Compras regulares" secondary="O que compras de tempos a tempos" />
        </MenuItem>
        <MenuItem onClick={more(() => navigate('/casa/compras/lojas'))}>
          <ListItemText primary="Lojas" secondary="Onde compras e a ordem dos corredores" />
        </MenuItem>
        <MenuItem onClick={more(planning.startNewTrip)}>
          <ListItemText primary="Nova ida" secondary="Levar a lista a uma loja" />
        </MenuItem>
        <MenuItem onClick={more(planning.split)}>
          <ListItemText primary="Dividir por lojas" secondary="Uma ida por loja com o que há para comprar" />
        </MenuItem>
      </Menu>

      <Loader loading={loading} error={error} onRetry={reload} hasData={data !== undefined}>
        {data && data.dueRecurring > 0 && (
          <Alert
            severity="info" icon={<EventRepeatIcon />} sx={{ mb: 2, borderRadius: 3 }}
            action={<Button color="inherit" size="small" onClick={() => navigate('/casa/compras/regulares')}>Rever</Button>}
          >
            {data.dueRecurring === 1 ? '1 compra regular está na altura de voltar à lista' : `${data.dueRecurring} compras regulares estão na altura de voltar à lista`}
          </Alert>
        )}
        {error && data && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        {data && needs.length === 0 && (
          <Paper variant="outlined">
            <EmptyState
              icon={<ShoppingCartOutlinedIcon />}
              title="Nada por comprar"
              text="O que fica abaixo do mínimo em casa entra sozinho. Também podes juntar os ingredientes da semana, ou dizer «falta leite»."
              action={<Button variant="contained" onClick={() => navigate('/casa/compras/da-ementa')}>Gerar da ementa</Button>}
            />
          </Paper>
        )}

        {needs.length > 0 && (
          <ToggleButtonGroup size="small" exclusive value={groupBy} onChange={(_, v) => changeGroupBy(v)} sx={{ mb: 2 }} fullWidth={isMobile}>
            <ToggleButton value="section">Por secção</ToggleButton>
            <ToggleButton value="store">Por loja</ToggleButton>
          </ToggleButtonGroup>
        )}

        <Columns>
          {groupNeeds(needs, groupBy).map(group => (
            <Box key={group.key} sx={{ mb: 2.5 }}>
              <Panel title={group.title} action={<Typography variant="caption" color="text.secondary">{group.items.length}</Typography>}>
                <List disablePadding>
                  {group.items.map((need, i) => (
                    <NeedRow key={need.publicId} need={need} groupBy={groupBy} onBought={bought} onOpen={setActionsFor} last={i === group.items.length - 1} />
                  ))}
                </List>
              </Panel>
            </Box>
          ))}
        </Columns>
      </Loader>

      {/* Everything that can be done with one item of the list */}
      <Dialog open={actionsFor !== null} onClose={() => setActionsFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: actionsFor && needDetail(actionsFor) ? 0 : undefined }}>{actionsFor?.name}</DialogTitle>
        {actionsFor && needDetail(actionsFor) && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1 }}>{needDetail(actionsFor)}</Typography>
        )}
        <List sx={{ pt: 0 }}>
          <ListItemButton onClick={() => choose('bought')}>
            <ListItemIcon><CheckIcon color="primary" /></ListItemIcon>
            <ListItemText primary="Comprado" slotProps={{ primary: { color: 'primary', fontWeight: 600 } }} />
          </ListItemButton>
          <ListItemButton onClick={() => choose('edit')}>
            <ListItemIcon><EditIcon /></ListItemIcon>
            <ListItemText primary="Quantidade e nota" secondary={actionsFor && needAmount(actionsFor) ? needAmount(actionsFor) : undefined} />
          </ListItemButton>
          <ListItemButton onClick={() => choose('store')}>
            <ListItemIcon><StoreIcon /></ListItemIcon>
            <ListItemText primary="Onde comprar" secondary={actionsFor?.storeName ?? 'Qualquer loja'} />
          </ListItemButton>
          <ListItemButton onClick={() => choose('item')}>
            <ListItemIcon><Inventory2Icon /></ListItemIcon>
            <ListItemText primary="Artigo" secondary="Nome, secção, outros nomes" />
          </ListItemButton>
          <ListItemButton onClick={() => choose('remove')}>
            <ListItemIcon><DeleteIcon color="error" /></ListItemIcon>
            <ListItemText primary="Retirar da lista" slotProps={{ primary: { color: 'error' } }} />
          </ListItemButton>
        </List>
        <DialogActions><Button onClick={() => setActionsFor(null)}>Fechar</Button></DialogActions>
      </Dialog>

      {/* Where to buy it: a store, or any */}
      <Dialog open={storeFor !== null} onClose={() => assignStore(undefined)} maxWidth="xs" fullWidth>
        <DialogTitle>Onde comprar {storeFor?.need.name}?</DialogTitle>
        <List sx={{ pt: 0 }}>
          <ListItemButton selected={!storeFor?.need.storePublicId} onClick={() => assignStore(null)}>
            <ListItemIcon><StoreIcon /></ListItemIcon>
            <ListItemText primary="Qualquer loja" />
          </ListItemButton>
          {(storeFor?.stores ?? []).map(s => (
            <ListItemButton key={s.publicId} selected={storeFor?.need.storePublicId === s.publicId} onClick={() => assignStore(s.publicId)}>
              <ListItemIcon><StoreIcon /></ListItemIcon>
              <ListItemText primary={s.name} />
            </ListItemButton>
          ))}
        </List>
        <DialogActions><Button onClick={() => assignStore(undefined)}>Cancelar</Button></DialogActions>
      </Dialog>

      <EditNeedDialog need={editing} onClose={saved => { setEditing(null); if (saved) reload(); }} />
      <PredictionDialog prediction={predicting} onClose={changed => { setPredicting(null); if (changed) reload(); }} />
      {planning.element}
    </>
  );
};

function NeedRow({ need, groupBy, onBought, onOpen, last }: {
  need: T.ShoppingNeed; groupBy: GroupBy; onBought: (n: T.ShoppingNeed) => void; onOpen: (n: T.ShoppingNeed) => void; last?: boolean;
}) {
  const source = needSourceChip(need.source);
  const detail = needDetail(need);
  const chips: React.ReactNode[] = [];
  if (need.status === 'InTrip') chips.push(<Chip key="trip" size="small" color="success" variant="outlined" label="numa ida" />);
  if (source) chips.push(<Chip key="source" size="small" variant="outlined" label={source} />);
  if (need.storeName && groupBy === 'section') chips.push(<Chip key="store" size="small" color="info" variant="outlined" label={need.storeName} />);
  if (groupBy === 'store') chips.push(<Chip key="section" size="small" variant="outlined" label={sectionText(need.storeSection)} />);
  if (need.lastUnavailableStoreName)
    chips.push(<Chip key="unavailable" size="small" color="warning" variant="outlined" label={`não havia no ${need.lastUnavailableStoreName}`} />);

  return (
    <ListItem divider={!last} disablePadding sx={{ alignItems: 'stretch' }}>
      <Tooltip title="Comprado">
        <IconButton aria-label={`${need.name}: comprado`} onClick={() => onBought(need)} sx={{ borderRadius: 0, px: 1.5 }} color="primary">
          <RadioButtonUncheckedIcon />
        </IconButton>
      </Tooltip>
      <ListItemButton onClick={() => onOpen(need)} sx={{ pl: 0.5, minWidth: 0 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0, py: 0.25 }}>
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Typography sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{need.name}</Typography>
            <Typography sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{needAmount(need)}</Typography>
          </Stack>
          {chips.length > 0 && <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>{chips}</Stack>}
          {detail && <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ mt: 0.25 }}>{detail}</Typography>}
        </Box>
      </ListItemButton>
    </ListItem>
  );
}

/** Quantity, unit and note of one item of the list. */
function EditNeedDialog({ need, onClose }: { need: T.ShoppingNeed | null; onClose: (saved: boolean) => void }) {
  const run = useRun();
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState<T.MeasureUnit | ''>('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [shown, setShown] = useState<string | null>(null);

  if (need && shown !== need.publicId) {
    setShown(need.publicId);
    setQuantity(need.quantity != null ? String(need.quantity).replace('.', ',') : '');
    setUnit(need.unit ?? '');
    setNote(need.note ?? '');
    setError(null);
  }
  if (!need && shown !== null) setShown(null);

  const save = async () => {
    if (!need) return;
    let value: number | null = null;
    if (quantity.trim()) {
      value = parseNumber(quantity);
      if (value === null || value <= 0) { setError('A quantidade deve ser um número positivo.'); return; }
    }
    setSaving(true);
    const result = await run(() => api.updateShoppingNeed(need.publicId, { quantity: value, unit: unit || null, note: note.trim() || null }));
    setSaving(false);
    if (result) onClose(true);
  };

  return (
    <Dialog open={need !== null} onClose={() => onClose(false)} maxWidth="xs" fullWidth>
      <DialogTitle>{need?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={1.5}>
            <TextField
              label="Quantidade" value={quantity} onChange={e => setQuantity(e.target.value)} placeholder="Nenhuma" autoFocus
              slotProps={{ htmlInput: { inputMode: 'decimal' } }} fullWidth
            />
            <TextField select label="Unidade" value={unit} onChange={e => setUnit(e.target.value as T.MeasureUnit | '')} fullWidth>
              <MenuItem value="">—</MenuItem>
              {UNITS.map(u => <MenuItem key={u.value} value={u.value}>{u.text} ({unitShort(u.value)})</MenuItem>)}
            </TextField>
          </Stack>
          <TextField
            label="Nota (opcional)" value={note} onChange={e => setNote(e.target.value)} placeholder="Ex.: marca, sem lactose"
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ShoppingPage;
